import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import VMasker from 'vanilla-masker';
import { FontAwesomeIcon } from '@vr/lib';
import styled, { css } from 'styled-components';
import isMobile from 'is-mobile';
import getNomeProduto from '../helpers/getNomeProduto';
import getIconByProduto from '../helpers/getIconByProduto';
import { DELIVERY_COLORS } from '../config/constants';

const sharedCssHoverActive = css`
  h3 {
    color: ${({ theme }) => theme.colors.institucional};
  }
`;

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  background-color: #fff;
  margin-bottom: 25px;
  border-radius: 10px;
  padding: 15px 0 ${({ openDelivery }) => (openDelivery ? '20px' : 0)};
  box-shadow: 0px 0px 8px #00000022;
  font-size: 12px;
  line-height: 24px;
  text-transform: capitalize;
  h3 {
    font-size: 15px;
    font-weight: bold;
    color: ${({ theme }) => theme.colors.gray400};
    margin-bottom: 12px;
  }
  ul {
    margin-top: 15px;
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    li {
      display: flex;
      flex: 1;
      align-items: center;
      font-size: 11px;
      font-weight: bold;
      color: ${({ theme }) => theme.colors.gray400};
      margin: 3px 12px 3px 0;
      white-space: nowrap;
      img {
        margin-right: 8px;
        width: 18px;
      }
      span {
        overflow: hidden;
        text-overflow: ellipsis;
      }
      &:nth-child(even) {
        margin-right: 0;
        width: 50%;
      }
    }
  }
  ${({ active }) => active && sharedCssHoverActive};
  &:hover {
    ${sharedCssHoverActive}
  }
`;
const Details = styled.div`
  cursor: pointer;
  width: 100%;
  margin-bottom: 5px;
  padding: 0 20px;
`;
const Delivery = styled.div`
  display: flex;
  flex-direction: column;
  transition: max-height 0.2s linear;
  max-height: ${({ openDelivery }) => (openDelivery ? '500px' : 0)};
  overflow: hidden;
  width: 100%;
  border-top: ${({ theme, openDelivery }) =>
    openDelivery ? `1px solid ${theme.colors.gray100}` : 'none'};
  margin-top: 18px;
`;
const DeliveryButton = styled.button`
  cursor: pointer;
  position: absolute;
  bottom: -30px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: 145px;
  height: 24px;
  background-color: ${({ theme }) => theme.colors.institucional};
  color: ${({ theme }) => theme.colors.white};
  text-transform: uppercase;
  font-size: 0.75rem;
  border: none;
  border-radius: 50px;
  font-weight: 700;
`;
const ChevronIcon = styled(FontAwesomeIcon)`
  margin: 2px 8px 0 0;
  transition: transform 0.2s linear;
  transform: ${({ openDelivery }) =>
    openDelivery ? 'rotate(180deg)' : 'rotate(0)'};
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin: 8px 0;
  padding: 0 20px;
`;
const VRPagueLink = styled.a`
  font-size: 0.9rem;
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.cultura};
  align-self: flex-start;
`;
const Label = styled.p`
  color: ${({ theme }) => theme.colors.gray400};
  font-size: 0.75rem;
  line-height: 24px;
  font-weight: 600;
  text-transform: none;

  span {
    color: ${({ theme }) => theme.colors.institucional};
    font-weight: bold;
    font-size: 0.9rem;
  }
`;
const Phone = styled.a`
  margin-top: 3px;
  text-decoration: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  align-self: flex-start;
  height: 25px;
  border-radius: 5px;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0 5px;
  color: ${({ theme }) => theme.colors.institucional};
  border: 2px solid ${({ theme }) => theme.colors.institucional};
  background-color: transparent;
`;
const PhoneIcon = styled(FontAwesomeIcon)`
  margin-right: 5px;
`;
const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 3px;
`;
const Option = styled.a`
  text-decoration: none;
  cursor: pointer;
  border: 2px solid ${({ name }) => DELIVERY_COLORS[name]};
  border-radius: 50px;
  height: 24px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  color: ${({ name }) => DELIVERY_COLORS[name]};
  margin-right: 16px;
  font-size: 0.75rem;
  font-weight: 700;
`;
const Header = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

function ResultItem({ item, onClick, active, companyName }) {
  const [openDelivery, setOpenDelivery] = useState(false);
  const renderProducts = useCallback(() => {
    if (!item.produtos || !Array.isArray(item.produtos)) {
      return null;
    }

    return (
      <ul>
        {item.produtos.map(code => {
          const nomeProduto = getNomeProduto(code);
          const iconProduto = getIconByProduto(code);

          return (
            <li key={`${item.id}-product-${code}`}>
              <img src={iconProduto} alt={nomeProduto} title={nomeProduto} />
              {companyName && `${companyName} `}
              {nomeProduto}
            </li>
          );
        })}
      </ul>
    );
  }, [item, companyName]);

  function getDeliveryLink(name) {
    const urls = {
      ifood: {
        desktop: 'https://www.ifood.com.br/',
        mobile: 'http://onelink.to/2vq2pw',
      },
      rappi: {
        desktop: 'https://www.rappi.com.br/',
        mobile: 'http://onelink.to/w8sd8b',
      },
      ubereats: {
        desktop: 'https://www.ubereats.com/',
        mobile: 'http://onelink.to/urpudw',
      },
    };

    return isMobile() ? urls[name].mobile : urls[name].desktop;
  }

  const getPhone = phone => {
    const purePhone = phone.replace(/\D/g, '').replace(/^0/, '');
    const maskPattern =
      purePhone.length > 10 ? '(99) 99999-9999' : '(99) 9999-9999';

    return VMasker.toPattern(purePhone, maskPattern);
  };

  const hasAnyDelivery =
    Boolean(item.rappi) || Boolean(item.ifood) || Boolean(item.ubereats);

  return (
    <Container
      id={`estabelecimento-${item.id}`}
      active={active}
      openDelivery={openDelivery}
    >
      <Header>
        <Details onClick={onClick}>
          <h3>{String(item.nome).toLowerCase()}</h3>
          {String(item.endereco).toLowerCase()}
          {renderProducts()}
        </Details>
        {Boolean(item.vrpague) && (
          <Content>
            <Label>
              Realize sua compra via <span>VR Pague.</span>
            </Label>
            <Label>Peça o link direto para o estabelecimento.</Label>
            <VRPagueLink
              href="https://www.vr.com.br/pra-voce/vr-pague.htm"
              target="_blank"
            >
              Saiba como usar.
            </VRPagueLink>
          </Content>
        )}
        {Boolean(item.delivery) && (
          <DeliveryButton onClick={() => setOpenDelivery(!openDelivery)}>
            <ChevronIcon
              openDelivery={openDelivery}
              icon={['fas', 'chevron-down']}
            />
            {!openDelivery ? 'faz entregas' : 'fechar'}
          </DeliveryButton>
        )}
      </Header>
      <Delivery openDelivery={openDelivery}>
        {Boolean(item.telefone) && (
          <Content>
            <Label>Telefone</Label>
            <Phone href={`tel:+550${item.ddd}${item.telefone}`}>
              <PhoneIcon icon={['fas', 'phone']} />
              {getPhone(item.ddd + item.telefone)}
            </Phone>
          </Content>
        )}
        {hasAnyDelivery && (
          <Content>
            <Label>Faça seu pedido on-line com:</Label>
            <Options>
              {Boolean(item.ifood) && (
                <Option
                  href={getDeliveryLink('ifood')}
                  target="_blank"
                  name="ifood"
                >
                  ifood
                </Option>
              )}
              {Boolean(item.rappi) && (
                <Option
                  href={getDeliveryLink('rappi')}
                  target="_blank"
                  name="rappi"
                >
                  rappi
                </Option>
              )}
              {Boolean(item.ubereats) && (
                <Option
                  href={getDeliveryLink('ubereats')}
                  target="_blank"
                  name="ubereats"
                >
                  uber eats
                </Option>
              )}
            </Options>
          </Content>
        )}
      </Delivery>
    </Container>
  );
}

ResultItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    nome: PropTypes.string,
    endereco: PropTypes.string,
    produtos: PropTypes.arrayOf(PropTypes.number),
    vrpague: PropTypes.number,
    delivery: PropTypes.number,
    ddd: PropTypes.string,
    telefone: PropTypes.string,
    ifood: PropTypes.number,
    rappi: PropTypes.number,
    ubereats: PropTypes.number,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  companyName: PropTypes.string.isRequired,
};

export default ResultItem;
